import { useQuery } from 'react-query'

import queryClient from '../../queryClient'
import { publicApiRequest } from '../../global/api/api'
// eslint-disable-next-line import-x/no-relative-packages
import defaultDesign from '../../../../studios/src/modules/webinar/modules/templates/blocs/templates.json'
import { getLiveData, useLiveDataQuery } from '../../global/blocs/useLiveData'
import { getSiteData } from '../../global/blocs/useSite'

const WEBINAR_DESIGNS_QUERY_KEYS = 'webinarDesigns'

const webinarDesignsKeys = {
  all: [WEBINAR_DESIGNS_QUERY_KEYS] as const,
  get: (id: number | 'default') => [WEBINAR_DESIGNS_QUERY_KEYS, id] as const,
}

type WebinarDesign = typeof defaultDesign

const useWebinarDesignQuery = () => {
  const { data: liveData } = useLiveDataQuery()

  const designId = liveData?.webinar_design_id || 'default'

  return useQuery({
    queryKey: webinarDesignsKeys.get(designId),
    queryFn: async (): Promise<WebinarDesign> => {
      let design
      try {
        const response = await publicApiRequest(
          `webinar-designs/${designId}`,
          'get'
        )
        design = response.data
      } catch (e) {
        // @ts-expect-error — TODO: type this properly
        if (e.response.statusCode !== 404) {
          throw e
        }
        design = defaultDesign

        const siteData = await getSiteData()

        design.designData['room-settings'].colors = {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          accent: siteData.presentation_webinarRoomAccentColor || '#1EC95B',
          sidebar:
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            siteData.presentation_webinarRoomSidebarBackgroundColor ||
            '#282A29',
          tabs:
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            siteData.presentation_webinarRoomTabBackgroundColor || '#323433',
          background: '#000000',
        }

        design.designData['room-settings'].graphics = {
          logo: siteData.presentation_webinarRoomLogo
            ? `/files/${siteData.presentation_webinarRoomLogo}`
            : '',
        }
      }

      // despite what the api spec says, there is a possibility of no webinar design being available
      return design as WebinarDesign
    },
    enabled: liveData !== undefined,
    staleTime: Infinity,
    cacheTime: Infinity,
  })
}

// Non-hook read of the already-fetched design (same pattern as getSiteData,
// but without triggering a fetch — callers outside React tolerate undefined
// while the query hasn't resolved yet).
const getWebinarDesign = () => {
  const designId = getLiveData()?.webinar_design_id || 'default'
  return queryClient.getQueryData<WebinarDesign>(
    webinarDesignsKeys.get(designId)
  )
}

export { useWebinarDesignQuery, getWebinarDesign }
